import { groupBy, uniqBy } from 'lodash';
import moment from 'moment';
import fileDownload from 'js-file-download';

import { Nullable, SelectOption } from '~globals/types';
import { CustomersImportRowErrorType } from '~globals/types/enums';
import {
  CustomerBase,
  CustomerImporter,
  CustomerImportRowError,
  CustomerImportRowErrorGroup,
} from '~services/customer/types';
import { pluralize } from '~utils/commons';
import { CustomerPrediction } from '~hooks/useCustomersAutocomplete';

export const getCustomerIdentifier = (
  customer: Nullable<CustomerBase | CustomerPrediction>,
): string => {
  let output = '';

  if (customer) {
    output += customer.legalName;

    if (customer.code) {
      output += ` (${customer.code})`;
    }
  }

  return output;
};

export const getImporterCustomerFromCSVErrorAlert = (
  errorRows: number,
): string => {
  const errorRowsText = pluralize({
    singular: 'fila con problema',
    plural: 'filas con problemas',
    count: errorRows,
    includeCount: true,
  });

  return errorRowsText;
};

export const getImporterCustomerFromCSVAlertDescription = ({
  importedRows,
  totalRows,
}: CustomerImporter): string | string[] => {
  const customerProcessedText = pluralize({
    singular: 'contacto frecuente procesado',
    plural: 'contactos frecuentes procesados',
    count: totalRows,
    includeCount: true,
  });

  return `${importedRows} de ${customerProcessedText}`;
};

export const IMPORT_GEO_ERROR_TYPES: CustomersImportRowErrorType[] = [
  CustomersImportRowErrorType.InvalidCoordinates,
  CustomersImportRowErrorType.GeocodingFailed,
  CustomersImportRowErrorType.GeocodingInaccurate,
];

export const IMPORT_ROW_ERROR_TYPE_LABELS: Record<
  CustomersImportRowErrorType,
  string
> = {
  [CustomersImportRowErrorType.MissingClientCode]:
    'Falta el código de contacto',
  [CustomersImportRowErrorType.MissingColumns]: 'Tiene menos de 18 columnas',
  [CustomersImportRowErrorType.MissingAddress]: 'Falta dirección',
  [CustomersImportRowErrorType.InvalidCoordinates]: 'Coordenadas inválidas',
  [CustomersImportRowErrorType.GeocodingFailed]: 'Dirección no encontrada',
  [CustomersImportRowErrorType.GeocodingInaccurate]: 'Ubicación aproximada',
  [CustomersImportRowErrorType.InvalidTimeWindow]:
    'Ventana horaria invalida (formato HH:mm)',
  [CustomersImportRowErrorType.UnexpectedError]: 'Error inesperado',
};

export const getImportRowErrorTypeLabel = (
  errorTypeId: CustomersImportRowErrorType,
  fallback = '',
): string => IMPORT_ROW_ERROR_TYPE_LABELS[errorTypeId] ?? fallback;

export const hasGeoImportErrors = (errors: CustomerImportRowError[]): boolean =>
  errors.some((error) => IMPORT_GEO_ERROR_TYPES.includes(error.errorTypeId));

export const groupImportErrorsByType = (
  errors: CustomerImportRowError[],
): CustomerImportRowErrorGroup[] => {
  const groupedByType = groupBy(errors, (error) => error.errorTypeId);

  return Object.keys(groupedByType)
    .map((typeKey) => {
      const typeErrors = groupedByType[typeKey];
      // A single file row can yield several entries of the same type (e.g.
      // latitude and longitude both out of range), so collapse them per row.
      const groupedByRow = groupBy(typeErrors, (error) => error.rowNumber);

      const rows = Object.keys(groupedByRow)
        .map((rowKey) => {
          const rowErrors = groupedByRow[rowKey];
          const [first] = rowErrors;

          return {
            rowNumber: first.rowNumber,
            clientCode: first.clientCode,
            legalName: first.legalName,
            messages: rowErrors.map((error) => error.message),
          };
        })
        .sort((a, b) => a.rowNumber - b.rowNumber);

      return {
        errorTypeId: Number(typeKey) as CustomersImportRowErrorType,
        errorType: typeErrors[0].errorType,
        rows,
        hasErrors: typeErrors.some((error) => !error.imported),
      };
    })
    .sort((a, b) => Number(b.hasErrors) - Number(a.hasErrors));
};

export const getImportErrorTypeOptions = (
  errors: CustomerImportRowError[],
): SelectOption[] =>
  uniqBy(errors, (error) => error.errorTypeId).map((error) => ({
    value: error.errorTypeId,
    label: getImportRowErrorTypeLabel(error.errorTypeId, error.errorType),
  }));

interface ImportErrorsCSVColumn {
  header: string;
  value: (error: CustomerImportRowError) => string | number;
}

const IMPORT_ERRORS_CSV_COLUMNS: ImportErrorsCSVColumn[] = [
  { header: 'Fila', value: (error) => error.rowNumber },
  {
    header: 'Código Cliente',
    value: (error) => error.clientCode,
  },
  { header: 'Nombre', value: (error) => error.legalName },
  {
    header: 'Tipo de Error',
    value: (error) =>
      getImportRowErrorTypeLabel(error.errorTypeId, error.errorType),
  },
  { header: 'Mensaje', value: (error) => error.message },
  { header: 'Importado', value: (error) => (error.imported ? 'Sí' : 'No') },
];

const IMPORT_ERRORS_CSV_DELIMITER = ';';

// Quote every field, escape embedded quotes, and neutralize spreadsheet
// formula injection by prefixing values that start with = + - @ with a quote.
const escapeImportErrorsCSVValue = (value: string | number): string => {
  const raw = String(value ?? '');
  const sanitized = /^[=+\-@]/.test(raw) ? `'${raw}` : raw;

  return `"${sanitized.replace(/"/g, '""')}"`;
};

export const buildImportErrorsCSV = (
  errors: CustomerImportRowError[],
): string => {
  const headerRow = IMPORT_ERRORS_CSV_COLUMNS.map((column) => column.header);
  const dataRows = errors.map((error) =>
    IMPORT_ERRORS_CSV_COLUMNS.map((column) => column.value(error)),
  );

  return [headerRow, ...dataRows]
    .map((row) =>
      row.map(escapeImportErrorsCSVValue).join(IMPORT_ERRORS_CSV_DELIMITER),
    )
    .join('\r\n');
};

export const downloadImportErrorsCSV = (
  errors: CustomerImportRowError[],
): void => {
  // Prepend a UTF-8 BOM so Excel opens accented characters correctly.
  const bomPrefix = String.fromCharCode(0xfeff);
  const content = `${bomPrefix}${buildImportErrorsCSV(errors)}`;
  const timestamp = moment().format('YYYYMMDD-HHmm');

  fileDownload(
    content,
    `errores-importacion-contactos-frecuentes-${timestamp}.csv`,
    'text/csv;charset=utf-8',
  );
};
